import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Panel from 'atoms/panel/Panel';
import Section from 'atoms/section/Section';
import Accordion from 'atoms/accordion/Accordion';
import styles from './SyncReportComponent.module.css';

const statusTranslations = {
  Ok: 'OK',
  Updated: 'Ändrad',
  Added: 'Tillagd',
  MissingInSas: 'Saknas i SAS'
};

const SyncReportComponent = ({ report }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showOnlyChanges, setShowOnlyChanges] = useState(true);

  const sync = report?.syncReport ?? report;

  if (!report) return null;

  const breakbys = showOnlyChanges
    ? report.breakbys.filter(b => b.hasChanges)
    : report.breakbys;

  const filters = showOnlyChanges
    ? report.filters.filter(f => f.hasChanges)
    : report.filters;

  const getStatusBadge = statusType => {
    switch (statusType) {
      case 'Error':
        return <span className={`${styles.badge} ${styles.badgeError}`}>Fel</span>;
      case 'Warning':
        return <span className={`${styles.badge} ${styles.badgeWarning}`}>Varning</span>;
      case 'Updated':
        return <span className={`${styles.badge} ${styles.badgeUpdated}`}>Ändrad</span>;
      default:
        return <span className={`${styles.badge} ${styles.badgeOk}`}>OK</span>;
    }
  };

  return (
    <>
      {/* Toggle button */}
      <div className={styles.syncToggleRow}>
        <button
          className={styles.tableToggle}
          onClick={() => setIsOpen(prev => !prev)}
        >
          {isOpen ? 'Dölj synkrapport' : 'Visa synkrapport'}
        </button>
      </div>

      {isOpen && (
        <Panel>
          <Section title="Synkrapport">

            <div className={styles.infoBox}>

              <div className={styles.infoHeader}>
                <div className={styles.statusBlock}>
                  <span className={styles.statusLabel}>Status</span>
                  <span className={`${styles.badge} ${styles.badgeOk}`}>
                    {statusTranslations[sync?.overallStatus] ?? sync?.overallStatus ?? 'OK'}
                  </span>
                </div>

                <button
                  className={styles.tableToggle}
                  onClick={() => setShowOnlyChanges(prev => !prev)}
                >
                  {showOnlyChanges
                    ? 'Visa alla poster'
                    : 'Visa endast ändringar'}
                </button>
              </div>

              {(sync?.totalMetadataChanges ?? 0) === 0 && (
                <div className={styles.successMessage}>
                  <span className={styles.checkIcon}>✓</span>
                  Inga avvikelser hittades mellan SAS och Lönesök.
                </div>
              )}

              {(sync?.totalMetadataChanges ?? 0) > 0 && (
                <div className={styles.changeInfoMessage}>
                  Identifierade skillnader uppdateras inte automatiskt. Vid klick på Spara kommer värden från SAS att ersätta motsvarande värden i Lönesök.
                </div>              
              )}

              <div className={styles.compactStats}>
                <div><strong>{sync?.totalBreakbys}</strong> Breakbys</div>
                <div><strong>{sync?.totalFilters}</strong> Filters</div>
                <div><strong>{sync?.totalBreakbyChanges}</strong> Breakby ändringar</div>
                <div><strong>{sync?.totalFilterChanges}</strong> Filter ändringar</div>
                <div><strong>{sync?.totalMetadataChanges}</strong> Totala ändringar</div>
                <div><strong>{sync?.totalOptionsLoaded}</strong> Options laddade</div>
              </div>

            </div>

            {/* Breakbys */}
            <Accordion title={`Breakbys (${breakbys.length})`} boxShadow marginBottom initialOpen={sync?.totalBreakbyChanges > 0 || sync?.totalMetadataChanges === 0}>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>BreakById</th>
                      <th>SAS Var</th>
                      <th>Lönesök Var</th>
                      <th>SAS Text</th>
                      <th>Lönesök Text</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakbys.map(b => (
                      <tr key={b.id}>
                        <td>{b.id}</td>
                        <td>{b.sasVar}</td>
                        <td>{b.dbVarOld}</td>
                        <td>{b.sasText}</td>
                        <td>{b.dbTextOld}</td>
                        <td>{getStatusBadge(b.hasChanges ? 'Updated' : b.statusType)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Accordion>

            {/* Filters */}
            <Accordion title={`Filters (${filters.length})`} boxShadow marginBottom initialOpen={sync?.totalFilterChanges > 0}>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>SelectionId</th>
                      <th>SAS Var</th>
                      <th>Lönesök Var</th>
                      <th>SAS Text</th>
                      <th>Lönesök Text</th>
                      <th>Options</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filters.map(f => (
                      <tr key={f.selectionId}>
                        <td>{f.selectionId}</td>
                        <td>{f.sasVar}</td>
                        <td>{f.dbVarOld}</td>
                        <td>{f.sasText}</td>
                        <td>{f.dbTextOld}</td>
                        <td>{f.optionCount}</td>
                        <td>{getStatusBadge(f.hasChanges ? 'Updated' : f.statusType)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Accordion>

          </Section>
        </Panel>
      )}
    </>
  );
};

SyncReportComponent.propTypes = {
  report: PropTypes.object,
};

export default SyncReportComponent;